import { useState, useEffect, ChangeEvent } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { StyledButton } from 'components/atoms/button'
import { Text } from 'components/atoms/text'
import { useAmp } from 'next/amp'
import { useIntl } from 'react-intl'

import { PATHS } from '../../../api/paths'
import { callApi, getApiPath } from '../../../api/utils'
import {
  getFeedbackUuid,
  setFeedbackUuid,
  checkInteractionArticle,
  setInteractionArticle,
} from '../../pages/article/utils'
import { FeedbackQuestion } from './feedbackQuestion'
import {
  StyledFeedbackPopup,
  StyledDarkBackground,
  StyledModalTop,
  CloseButton,
  StyledModalContent,
  StyledSubmitContainer,
} from './index.styled'

export type Props = {
  className?: string
  isLike?: boolean
  articleId?: string | number
  show?: boolean
  onHidden?: () => void
  onSuccess?: () => void
}

export type QuestionDataType = {
  icon: string
  iconBackgroundColor: string
  value?: string
}

const FeedbackPopup = ({
  className,
  articleId,
  isLike,
  show,
  onHidden,
  onSuccess,
}: Props) => {
  const { formatMessage: f } = useIntl()
  const [openIndex, setOpenIndex] = useState(-1)
  const [questions, setQuestions] = useState<QuestionDataType[]>([
    {
      icon: isLike ? '/icons/heart_solid.svg' : '/icons/disclaimer_solid.svg',
      iconBackgroundColor: isLike
        ? theme.mbColors.lightPink
        : theme.mbColors.lightOrange,
    },
    {
      icon: isLike
        ? '/icons/thumbsup_solid.svg'
        : '/icons/thumbsdown_solid.svg',
      iconBackgroundColor: theme.mbColors.lightBlue,
    },
    {
      icon: '/icons/question_solid.svg',
      iconBackgroundColor: theme.mbColors.lightMint,
    },
  ])
  const disabledSubmit = openIndex === 2 && !questions[2].value

  useEffect(() => {
    if (isLike) {
      questions[0].icon = '/icons/heart_solid.svg'
      questions[1].icon = '/icons/thumbsup_solid.svg'
    } else {
      questions[0].icon = '/icons/disclaimer_solid.svg'
      questions[1].icon = '/icons/thumbsdown_solid.svg'
    }
    setQuestions(questions.slice())
  }, [isLike])

  const onSendOpinion = () => {
    if (disabledSubmit || !articleId) return
    const sendData = {
      feedback: isLike ? '1' : '2',
      feedback_stat: '1',
      message: '',
    }

    questions.forEach((q, index) => {
      if (q.value) {
        sendData.message = q.value
        sendData.feedback_stat = String(index + 1)
      }
    })

    const uuid = getFeedbackUuid()
    const formdata = new FormData()
    formdata.append('feedback', sendData.feedback)
    formdata.append('feedback_stat', sendData.feedback_stat)
    formdata.append('message', sendData.message)
    formdata.append('uuid', uuid || '')

    callApi(
      getApiPath(PATHS.POST_ARTICLE_FEEDBACK, {
        id: articleId,
      }),
      'POST',
      {
        data: formdata,
      },
    )
      .then((result) => {
        const hhg_content_feedback_user =
          result?.data?.hhg_content_feedback_user
        if (!uuid) {
          setFeedbackUuid(hhg_content_feedback_user)
        }
        if (!checkInteractionArticle(articleId, isLike)) {
          setInteractionArticle(articleId, isLike)
        }
        closeForm()
        onSuccess && onSuccess()
      })
      .catch((error) => {
        console.log('error', error)
        closeForm()
      })
  }

  const closeForm = () => {
    questions[0].value = ''
    questions[1].value = ''
    questions[2].value = ''
    setQuestions(questions.slice())
    onHidden && onHidden()
  }

  const onInputValueChange = (
    index: number,
    e: ChangeEvent<HTMLTextAreaElement>,
  ) => {
    questions[index].value = e.target.value
    setQuestions(questions.slice())
  }

  useEffect(() => {
    if (!show) {
      setOpenIndex(-1)
    }
  }, [show])

  const isAmp = useAmp()
  return (
    <>
      <StyledDarkBackground
        data-show={show}
        onClick={(e) => {
          e.stopPropagation()
          closeForm()
        }}
      >
        <StyledFeedbackPopup
          onClick={(e) => {
            e.stopPropagation()
          }}
          data-show={show}
          className={className}
        >
          <StyledModalTop>
            <Text type="subtitle-1">
              {f({
                id: isLike
                  ? 'article.feedback.like.title'
                  : 'article.feedback.dislike.title',
              })}
            </Text>
            <CloseButton onClick={closeForm}>
              {!isAmp ? (
                <img
                  loading="lazy"
                  src={'/images/close.svg'}
                  style={{ width: '24px', height: '24px' }}
                  alt="close"
                />
              ) : (
                <amp-img
                  src="/images/close.svg"
                  width="24"
                  height="24"
                  alt="close"
                />
              )}
            </CloseButton>
          </StyledModalTop>
          <StyledModalContent>
            {questions.map((questionData, index) => {
              return (
                <FeedbackQuestion
                  key={index}
                  index={index}
                  isLike={isLike}
                  data={questionData}
                  isOpen={openIndex === index}
                  onOpenClick={() =>
                    setOpenIndex(index === openIndex ? -1 : index)
                  }
                  onInputValueChange={(e) => onInputValueChange(index, e)}
                />
              )
            })}

            <StyledSubmitContainer>
              <StyledButton
                color="primary"
                size="lg"
                isDisabled={disabledSubmit}
                onClick={onSendOpinion}
              >
                {f({
                  id: 'article.feedback.submit',
                })}
              </StyledButton>
            </StyledSubmitContainer>
          </StyledModalContent>
        </StyledFeedbackPopup>
      </StyledDarkBackground>
    </>
  )
}

export { FeedbackPopup }
